import React, { useState, useEffect } from 'react';
import { getSuppliers, createSupplier, searchSuppliers } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Globe, Mail, Phone, MapPin, Loader2, Building } from 'lucide-react';

function SupplierListPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cat, setCat] = useState('');
  const [specs, setSpecs] = useState('');
  const [loc, setLoc] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [nsName, setNsName] = useState('');
  const [nsEmail, setNsEmail] = useState('');
  const [nsPhone, setNsPhone] = useState('');
  const [nsAddress, setNsAddress] = useState('');
  const [nsSpec, setNsSpec] = useState('');

  useEffect(function() { load(); }, []);

  async function load() {
    try { const r = await getSuppliers(); setSuppliers(r.data); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function add() {
    try {
      await createSupplier({ name: nsName, email: nsEmail, phone: nsPhone, address: nsAddress, specialization: nsSpec.split(',').map(function(s) { return s.trim(); }) });
      toast.success('تمت الإضافة');
      setAddOpen(false);
      setNsName(''); setNsEmail(''); setNsPhone(''); setNsAddress(''); setNsSpec('');
      load();
    } catch(e) { toast.error('فشل'); }
  }

  async function search() {
    setSearching(true);
    try { const r = await searchSuppliers(cat, specs, loc); setResults(r.data.suppliers || []); }
    catch(e) { toast.error('فشل'); }
    finally { setSearching(false); }
  }

  const filtered = suppliers.filter(function(s) { return !term || (s.name && s.name.toLowerCase().includes(term.toLowerCase())) || (s.email && s.email.toLowerCase().includes(term.toLowerCase())); });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold uppercase">الموردين</h1><p className="text-muted-foreground mt-1">Suppliers</p></div>
        <div className="flex gap-2">
          <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="search-suppliers-btn"><Globe className="h-4 w-4 mr-2" />بحث</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>البحث عن موردين</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>الفئة</Label><Input value={cat} onChange={function(e) { setCat(e.target.value); }} placeholder="Aluminum" /></div>
                  <div><Label>الموقع</Label><Input value={loc} onChange={function(e) { setLoc(e.target.value); }} placeholder="UAE" /></div>
                </div>
                <div><Label>المواصفات</Label><Textarea value={specs} onChange={function(e) { setSpecs(e.target.value); }} rows={2} /></div>
                <Button onClick={search} disabled={searching || !cat} className="w-full" data-testid="search-btn">{searching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}بحث</Button>
                {results.length > 0 && <div className="max-h-48 overflow-y-auto space-y-2">{results.map(function(r, i) { return <div key={i} className="p-3 bg-muted/30 rounded-sm border"><p className="font-medium">{r.name}</p><p className="text-sm text-muted-foreground">{r.email}</p></div>; })}</div>}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-2" />إضافة</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>مورد جديد</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>الاسم *</Label><Input value={nsName} onChange={function(e) { setNsName(e.target.value); }} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>البريد *</Label><Input type="email" value={nsEmail} onChange={function(e) { setNsEmail(e.target.value); }} /></div>
                  <div><Label>الهاتف</Label><Input value={nsPhone} onChange={function(e) { setNsPhone(e.target.value); }} /></div>
                </div>
                <div><Label>العنوان</Label><Input value={nsAddress} onChange={function(e) { setNsAddress(e.target.value); }} /></div>
                <div><Label>التخصصات</Label><Input value={nsSpec} onChange={function(e) { setNsSpec(e.target.value); }} placeholder="Aluminum,Steel" /></div>
                <Button onClick={add} disabled={!nsName || !nsEmail} className="w-full" data-testid="save-supplier-btn">حفظ</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="بحث..." value={term} onChange={function(e) { setTerm(e.target.value); }} className="pl-10" data-testid="supplier-search-input" /></div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <div className="col-span-full flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> : filtered.length === 0 ? <div className="col-span-full text-center py-12 text-muted-foreground"><Building className="h-16 w-16 mx-auto mb-4 opacity-50" /><p>لا موردين</p></div> : filtered.map(function(s) { return (
          <Card key={s.id} className="bg-card border-border hover:border-primary/40 transition-all" data-testid={'supplier-card-' + s.id}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3"><div className="p-2 rounded-sm bg-muted"><Building className="h-5 w-5" /></div><h3 className="font-medium">{s.name}</h3></div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /><span className="truncate">{s.email}</span></div>
                {s.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /><span>{s.phone}</span></div>}
                {s.address && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /><span className="truncate">{s.address}</span></div>}
              </div>
              {s.specialization && s.specialization.length > 0 && <div className="flex flex-wrap gap-1 mt-3">{s.specialization.map(function(sp, i) { return <Badge key={i} variant="outline" className="text-xs">{sp}</Badge>; })}</div>}
            </CardContent>
          </Card>
        ); })}
      </div>
    </div>
  );
}

export default SupplierListPage;
